import DOMPurify from 'dompurify';
import validator from 'validator';

/**
 * Sanitization Utilities
 * Prevents XSS attacks and malicious input
 */

class Sanitizer {
  /**
   * Sanitize HTML content
   */
  static sanitizeHTML(dirty) {
    if (!dirty) return '';
    
    const config = {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'h3'],
      ALLOWED_ATTR: ['href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false,
      ALLOW_UNKNOWN_PROTOCOLS: false,
    };
    
    return DOMPurify.sanitize(dirty, config);
  }

  /**
   * Sanitize plain text (strip all HTML)
   */
  static sanitizeText(input) {
    if (!input) return '';
    return DOMPurify.sanitize(input, { ALLOWED_TAGS: [] });
  }

  /**
   * Sanitize URL
   */
  static sanitizeURL(url) {
    if (!url) return '';
    
    // Validate URL format
    if (!validator.isURL(url, { protocols: ['http', 'https'], require_protocol: true })) {
      return '';
    }
    
    // Additional XSS protection
    const dangerous = /javascript:|data:|vbscript:|file:/i;
    if (dangerous.test(url)) {
      return '';
    }
    
    return url;
  }

  /**
   * Sanitize email
   */
  static sanitizeEmail(email) {
    if (!email) return '';
    
    const sanitized = validator.normalizeEmail(email);
    return validator.isEmail(sanitized || '') ? sanitized : '';
  }

  /**
   * Sanitize phone number
   */
  static sanitizePhone(phone) {
    if (!phone) return '';
    
    // Remove all non-numeric characters
    const cleaned = phone.replace(/\D/g, '');
    return validator.isMobilePhone(cleaned) ? cleaned : '';
  }

  /**
   * Sanitize alphanumeric input
   */
  static sanitizeAlphanumeric(input) {
    if (!input) return '';
    return input.replace(/[^a-zA-Z0-9]/g, '');
  }

  /**
   * Escape special characters for regex
   */
  static escapeRegex(string) {
    if (!string) return '';
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Sanitize object recursively
   */
  static sanitizeObject(obj, options = {}) {
    if (!obj || typeof obj !== 'object') return obj;
    
    const { allowHTML = false, maxDepth = 10, currentDepth = 0 } = options;
    
    if (currentDepth >= maxDepth) return obj;
    
    const sanitized = Array.isArray(obj) ? [] : {};
    
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        
        if (typeof value === 'string') {
          sanitized[key] = allowHTML 
            ? this.sanitizeHTML(value) 
            : this.sanitizeText(value);
        } else if (typeof value === 'object' && value !== null) {
          sanitized[key] = this.sanitizeObject(value, {
            ...options,
            currentDepth: currentDepth + 1,
          });
        } else {
          sanitized[key] = value;
        }
      }
    }
    
    return sanitized;
  }

  /**
   * Validate and sanitize file name
   */
  static sanitizeFileName(fileName) {
    if (!fileName) return '';
    
    // Remove path separators and dangerous characters
    let sanitized = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
    
    // Prevent directory traversal
    sanitized = sanitized.replace(/\.\./g, '');
    
    // Limit length
    if (sanitized.length > 255) {
      const ext = sanitized.split('.').pop();
      sanitized = sanitized.substring(0, 250) + '.' + ext;
    }
    
    return sanitized;
  }

  /**
   * Sanitize JSON string
   */
  static sanitizeJSON(jsonString) {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(this.sanitizeObject(parsed));
    } catch (error) {
      console.error('Invalid JSON string:', error);
      return '';
    }
  }

  /**
   * Remove null bytes (can bypass some filters)
   */
  static removeNullBytes(input) {
    if (!input) return '';
    return input.replace(/\0/g, '');
  }

  /**
   * Comprehensive input sanitization
   */
  static sanitizeInput(input, type = 'text') {
    if (input === null || input === undefined) return '';
    
    // Remove null bytes first
    let sanitized = this.removeNullBytes(String(input));
    
    switch (type) {
      case 'html':
        return this.sanitizeHTML(sanitized);
      case 'url':
        return this.sanitizeURL(sanitized);
      case 'email':
        return this.sanitizeEmail(sanitized);
      case 'phone':
        return this.sanitizePhone(sanitized);
      case 'alphanumeric':
        return this.sanitizeAlphanumeric(sanitized);
      case 'filename':
        return this.sanitizeFileName(sanitized);
      case 'text':
      default:
        return this.sanitizeText(sanitized);
    }
  }
}

export default Sanitizer;
